import type { Database } from '../../types/supabase';
import type { NullableFields } from '../../types/rpcNullability';

/**
 * Relations Hub V1 — la « boîte intelligente » (LECTURE seule, PLAN-RELATIONS-HUB §4).
 *
 * Types + helpers PURS pour la boîte de réception classée. La donnée vient telle
 * quelle des RPC `get_relations_inbox` / `get_relations_inbox_counts` (pass-through
 * strict, garde-fou CLAUDE.md : on ne re-shape pas, on ne re-dérive pas la lecture
 * métier, on ne devine pas un vocabulaire). Ces helpers ne font que : borner la
 * lane URL sur le vocabulaire du backend, donner les libellés FR d'affichage, et
 * mapper une catégorie sur une couleur de badge (pur formatage).
 */

/**
 * Une ligne de la boîte, telle que renvoyée par `get_relations_inbox` — avec la
 * nullabilité réelle du SQL, que la génération ne porte pas : subject/category/
 * disposition/contact_id/account_id sont des colonnes nullables de `crm_emails`
 * (un mail pas encore classé lit `category = null`), account_name vient d'un
 * LEFT JOIN sur `crm_accounts`, et confidence est un littéral `null::numeric`.
 */
export type InboxRow = Omit<
  NullableFields<
    Database['public']['Functions']['get_relations_inbox']['Returns'][number],
    | 'account_id'
    | 'account_name'
    | 'category'
    | 'confidence'
    | 'contact_id'
    | 'disposition'
    | 'email_id'
    | 'subject'
  >,
  'in_inbox'
> & {
  /**
   * `email_id` null = le fil n'a aucun mail dans le Hub : c'est un premier mail
   * préparé par Otto, la conversation se réduit à son brouillon (lane « Suggestions
   * prêtes », v_pending_ai_drafts). `from_email` porte alors le destinataire.
   *
   * false = rangé par Otto hors de la boîte de réception (visible 7 jours dans sa lane de
   * catégorie). Absent de `get_relation_thread_header`, qui partage ce type.
   */
  in_inbox?: boolean;
};

/** Une ligne de compteur, telle que renvoyée par `get_relations_inbox_counts`. */
export type InboxCount =
  Database['public']['Functions']['get_relations_inbox_counts']['Returns'][number];

/**
 * Les lanes de classification, vocabulaire EXACT du backend (`p_lane`).
 * `all` = tout sauf le bruit (défaut). Une lane hors de cette liste n'existe pas.
 */
export type InboxLane =
  | 'all'
  | 'reply'
  | 'draft'
  | 'scheduled'
  | 'sent'
  | 'commercial'
  | 'partenaire'
  | 'support'
  | 'bruit'
  | 'finance'
  | 'perso';

/** Toutes les lanes connues (source de vérité d'affichage + validation URL). */
export const INBOX_LANES: readonly InboxLane[] = [
  'all',
  'reply',
  'draft',
  'scheduled',
  'sent',
  'commercial',
  'partenaire',
  'support',
  'bruit',
  'finance',
  'perso',
];

/** Lanes « ouvertes » du rail (pilotage quotidien). */
export const OPEN_LANES: readonly InboxLane[] = [
  'all',
  'reply',
  'draft',
  'scheduled',
  'sent',
  'commercial',
  'partenaire',
  'support',
  'bruit',
];

/** Lanes « confidentielles » (cloisonnées, cerveau masqué). */
export const CONFIDENTIAL_LANES: readonly InboxLane[] = ['finance', 'perso'];

/** Libellé FR de chaque lane (rail). */
export const LANE_LABELS: Record<InboxLane, string> = {
  all: 'Boîte de réception',
  reply: 'À répondre',
  draft: 'Suggestions prêtes',
  // Envois programmés vivants (programmés, en cours, retenus) — PLAN-MAIL-ENVOI-PROGRAMME.
  scheduled: 'Programmés',
  sent: 'Envoyés',
  commercial: 'Commercial',
  partenaire: 'Partenaires',
  // Same words as the Gmail labels Otto/* (_shared/gmailTriageLabels.ts): one vocabulary
  // for the Hub and Gmail. DB values stay `support` / `bruit` / `finance_confidentiel` /
  // `personnel`.
  support: 'Clients',
  bruit: 'Notifications',
  finance: 'Admin & Finance',
  perso: 'Perso',
};

/** Couleurs d'avatar déterministes (maquette). */
const AVATAR_COLORS = ['#2563EB', '#0E9C8B', '#8B5CF6', '#C77410', '#DB4C6B', '#0EA5E9', '#5B6B7F'] as const;

export function avatarColorFor(label: string): string {
  let hash = 0;
  for (const ch of label) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

export type InboxSort = 'prio' | 'date';

/** Tri client de la liste (maquette : Priorité / Date). */
export function sortInboxRows(rows: readonly InboxRow[], sort: InboxSort): InboxRow[] {
  const copy = [...rows];
  if (sort === 'date') {
    copy.sort((a, b) => new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime());
    return copy;
  }
  copy.sort((a, b) => {
    const score = (r: InboxRow) =>
      (r.needs_reply ? 4 : 0) + (r.has_ai_draft ? 2 : 0) + (r.is_read === false ? 1 : 0);
    const diff = score(b) - score(a);
    if (diff !== 0) return diff;
    return new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime();
  });
  return copy;
}

/** Couleur de catégorie pour les chips (alignée maquette). */
export function categoryColor(category: string | null): string {
  const map: Record<string, string> = {
    commercial: '#2563EB',
    partenaire: '#0E9C8B',
    support: '#C77410',
    finance_confidentiel: '#5B6B7F',
    personnel: '#8B5CF6',
    bruit: '#97A1AD',
  };
  return map[category ?? ''] ?? '#8792A0';
}

/** Parse la lane active depuis l'URL (défaut mail normal : boîte de réception). */
export function parseLane(raw: string | null): InboxLane {
  return (INBOX_LANES as readonly string[]).includes(raw ?? '')
    ? (raw as InboxLane)
    : 'all';
}

/**
 * L'argument `p_lane` à passer à la RPC pour une lane donnée. `all` → `null`
 * (le backend interprète NULL comme « tout sauf bruit » ; lui passer la chaîne
 * 'all' tomberait dans la branche « lane inconnue » → zéro résultat).
 */
export function laneParam(lane: InboxLane): string | null {
  return lane === 'all' ? null : lane;
}

/**
 * Après l'envoi ou l'abandon de la suggestion d'Otto d'un fil ouvert : le fil quitte-t-il
 * la vue ? Oui dans « Suggestions prêtes » (il n'y a plus sa place), et toujours pour un
 * premier mail : il est parti dans un nouveau fil Gmail, sa conversation-brouillon
 * n'existe plus. Ailleurs on reste sur le fil, qui montre le mail envoyé.
 */
export function leavesViewAfterDraftResolved(lane: InboxLane, draftOnly: boolean): boolean {
  return lane === 'draft' || draftOnly;
}

/** Indexe les compteurs RPC par lane (accès O(1), 0 par défaut). */
export function countsByLane(rows: readonly InboxCount[]): Record<string, number> {
  const map: Record<string, number> = {};
  for (const row of rows) map[row.lane] = Number(row.count);
  return map;
}

/**
 * Une conversation est confidentielle (hors cerveau) si elle est marquée perso
 * ou classée personnel. Dérivé du même vocabulaire que le backend —
 * on ne l'invente pas, on le lit (pass-through). Sert au cloisonnement du
 * panneau cerveau (PLAN-RELATIONS-HUB §5).
 */
export function isConfidential(
  row: Pick<InboxRow, 'category' | 'is_personal'>,
): boolean {
  // Admin & Finance feeds the brain since 27/09/2026: only private life stays walled off.
  return row.is_personal === true || row.category === 'personnel';
}

/** Vocabulaire de catégorie du backend (colonne `crm_emails.category`). */
export type EmailCategory =
  | 'commercial'
  | 'partenaire'
  | 'support'
  | 'bruit'
  | 'finance_confidentiel'
  | 'personnel';

interface CategoryTone {
  label: string;
  /** Classes Tailwind (fond + texte) light + dark, pour un badge doux. */
  className: string;
}

/**
 * Mapping catégorie → libellé + couleur de badge. Le fallback `unknown` couvre
 * un mail pas encore classé (`category = null`) ou toute valeur inattendue —
 * on n'invente pas de catégorie, on dégrade proprement en « Non classé ».
 */
const CATEGORY_TONES: Record<EmailCategory | 'unknown', CategoryTone> = {
  commercial: {
    label: 'Commercial',
    className: 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  },
  partenaire: {
    label: 'Partenaires',
    className: 'bg-violet-50 text-violet-700 dark:bg-violet-950 dark:text-violet-300',
  },
  support: {
    label: 'Clients',
    className: 'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
  },
  bruit: {
    label: 'Notifications',
    className: 'bg-neutral-100 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400',
  },
  finance_confidentiel: {
    label: 'Admin & Finance',
    className: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
  },
  personnel: {
    label: 'Perso',
    className: 'bg-rose-50 text-rose-700 dark:bg-rose-950 dark:text-rose-300',
  },
  unknown: {
    label: 'Non classé',
    className: 'bg-neutral-100 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400',
  },
};

/** Libellé + couleur d'une catégorie (pur ; fallback « Non classé »). */
export function categoryTone(category: string | null): CategoryTone {
  if (category && category in CATEGORY_TONES) {
    return CATEGORY_TONES[category as EmailCategory];
  }
  return CATEGORY_TONES.unknown;
}

/**
 * Les 6 catégories éditables depuis l'UI (override humain), dans l'ordre
 * d'affichage du menu. Vocabulaire EXACT du backend — on ne réordonne ni
 * n'invente : finance/perso sortent du cerveau, bruit est ignoré.
 */
export const EDITABLE_CATEGORIES: readonly EmailCategory[] = [
  'commercial',
  'partenaire',
  'support',
  'finance_confidentiel',
  'personnel',
  'bruit',
];

/** L'expéditeur affiché : société si rattachée, sinon l'adresse email. */
export function displaySender(row: Pick<InboxRow, 'account_name' | 'from_email'>): string {
  return row.account_name?.trim() || row.from_email;
}
